import React, { useState, useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Navbar from './components/Layout/Navbar';
import MobileBottomNav from './components/Layout/MobileBottomNav';
import SignInGateway from './components/Common/SignInGateway';
import TopInfoBar from './components/Common/TopInfoBar';
import AdminTopBar from './components/Layout/AdminTopBar';
import NoticeBar from './components/Common/NoticeBar';
import WhatsAppButton from './components/Common/WhatsAppButton';
import HelpLauncher from './components/Common/HelpLauncher';
import RouteFaqSection from './components/Common/RouteFaqSection';
import PrivateRoute from './components/Common/PrivateRoute';
import PageErrorBoundary from './components/Common/PageErrorBoundary';
import PageLoader from './components/Common/PageLoader';
import { AuthProvider } from './context/AuthContext';
import DynamicSEO from './components/Common/DynamicSEO';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { SiteBrandProvider } from './context/SiteBrandContext';
import { BareLayoutContext } from './context/BareLayoutContext';
import { ThemeCustomProvider } from './context/ThemeCustomContext';
import { HotelsProvider } from './context/HotelsContext';
import { PageContentProvider } from './context/PageContentContext';
import { B2BAgentProvider } from './context/B2BAgentContext';

import { API_BASE_URL } from './constants';
import { readSiteFeatures, isFeatureEnabled } from './utils/siteFeatures';
import { prefetchRoute } from './utils/prefetchRoutes';
import { getPublicConfig } from './utils/publicConfig';
import { consumeScrollRestoreClaim } from './utils/scrollRestoreClaim';
// index.css is already imported in index.js — no need to import it twice

// Heavy / below-the-fold components are lazy-loaded so they don't bloat the
// main chunk on first paint. Each saves 20-40KB minified from the initial
// download and defers their listener cost until they actually render.
// MUST be declared AFTER all static imports (eslint `import/first` rule).
const AIChatBot     = lazy(() => import('./components/ChatBot/ChatBot'));
const Footer        = lazy(() => import('./components/Layout/Footer'));
const VisitorPopup  = lazy(() => import('./components/Common/VisitorPopup'));
// Occasion campaign popup (Admin → Widgets → Occasion Campaign).
const CampaignPopup = lazy(() => import('./components/Campaign/CampaignPopup'));
const CampaignThemeStyle = lazy(() => import('./components/Campaign/CampaignDecor').then((m) => ({ default: m.CampaignThemeStyle }))); // CAMPAIGN-TEMP
const CampaignHeroFx = lazy(() => import('./components/Campaign/CampaignDecor').then((m) => ({ default: m.CampaignHeroFx }))); // CAMPAIGN-TEMP
const CookieBanner  = lazy(() => import('./components/Common/CookieBanner'));
// iPhone/iPad only — every other visitor used to download it in main.js.
const IOSInstallPrompt = lazy(() => import('./components/Common/IOSInstallPrompt'));
const AndroidAppBanner = lazy(() => import('./components/Common/AndroidAppBanner'));
const IS_IOS = typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/.test(navigator.userAgent || '') || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

// Shown by Suspense while a lazy chunk is still downloading.
// Stays visible until React unmounts it (i.e. when the chunk is ready).
// Intentionally has NO hooks so it can safely be a Suspense fallback.
const RouteLoadingFallback = () => {
    const dark = document.documentElement.classList.contains('dark');
    return (
        <div style={{
            position: 'fixed', inset: 0,
            zIndex: 9990,
            background: dark ? '#020617' : '#f8fafc',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                width: 44, height: 44, borderRadius: '50%',
                border: '3.5px solid #e2e8f0',
                borderTopColor: 'var(--hm-primary, #10b981)',
                animation: 'spin 0.75s linear infinite',
            }} />
        </div>
    );
};

// ── Lazy-loaded routes (each gets its own JS chunk, loaded only when visited) ──
// Home is lazy-loaded too — eagerly importing it bloated the main bundle by
// ~270KB gzipped (every homepage widget + section), which made EVERY route
// feel slow on first visit because the main chunk had to download first.
// HomeSwitch picks the home THEME by the admin-selected `homeVariant`
// (registry: src/pages/Home/variants.js). Each theme is its own lazy chunk,
// so a visitor downloads only the theme that actually renders.
const Home               = lazy(() => import('./pages/Home/HomeSwitch'));
const Hotels             = lazy(() => import('./pages/Hotels/Hotels'));
const Destinations       = lazy(() => import('./pages/Destinations/Destinations'));
const Experiences        = lazy(() => import('./pages/Experiences/Experiences'));
const SearchResults      = lazy(() => import('./components/Search/SearchResults/SearchResults'));
const NearbyHotelsPage   = lazy(() => import('./pages/NearbyHotelsPage/NearbyHotelsPage'));
const HotelDetailPage    = lazy(() => import('./pages/HotelDetailPage/HotelDetailPage'));
const ConfirmBookingPage = lazy(() => import('./pages/ConfirmBookingPage/ConfirmBookingPage'));
const WebBedsHotelDetail = lazy(() => import('./pages/WebBeds/WebBedsHotelDetail'));
const WebBedsBookingForm = lazy(() => import('./pages/WebBeds/WebBedsBookingForm'));
const HotelbedsHotelDetail = lazy(() => import('./pages/Hotelbeds/HotelbedsHotelDetail'));
const HotelbedsBookingForm = lazy(() => import('./pages/Hotelbeds/HotelbedsBookingForm'));
const PrivatePage        = lazy(() => import('./pages/Private/PrivatePage'));
const HelpCenterPage     = lazy(() => import('./pages/HelpCenter/HelpCenterPage'));
const About              = lazy(() => import('./pages/About/About'));
const Blog               = lazy(() => import('./pages/Blog/Blog'));
const BlogPost           = lazy(() => import('./pages/Blog/BlogPost'));
const MyBookings         = lazy(() => import('./pages/MyBookings/MyBookings'));
const FlightSearch       = lazy(() => import('./pages/Flights/FlightSearch'));
const FlightDetail       = lazy(() => import('./pages/Flights/FlightDetail'));
const PkfareSearch       = lazy(() => import('./pages/Flights/PkfareSearch'));
const ToursPage          = lazy(() => import('./pages/Tours/ToursPage'));
const TourDetail         = lazy(() => import('./pages/Tours/TourDetail'));
const TourBookingPage    = lazy(() => import('./pages/Tours/TourBookingPage'));
const ContactPage        = lazy(() => import('./pages/Contact/ContactPage'));
const WriteReviewPage    = lazy(() => import('./pages/WriteReview/WriteReviewPage'));
const CareersPage        = lazy(() => import('./pages/Careers/CareersPage'));
const PressPage          = lazy(() => import('./pages/Press/PressPage'));
const SitemapPage        = lazy(() => import('./pages/Sitemap/SitemapPage'));
const SupportPage        = lazy(() => import('./pages/Support/SupportPage'));
const TermsPage          = lazy(() => import('./pages/Terms/TermsPage'));
const PrivacyPage        = lazy(() => import('./pages/Privacy/PrivacyPage'));
const FAQPage            = lazy(() => import('./pages/FAQ/FAQPage'));
const DealsPage          = lazy(() => import('./pages/Deals/DealsPage'));
const DealDetailPage     = lazy(() => import('./pages/Deals/DealDetailPage'));
const VisaPage           = lazy(() => import('./pages/Visa/VisaPage'));
const HajjUmrahPage      = lazy(() => import('./pages/HajjUmrah/HajjUmrahPage'));
const HajjUmrahDetailPage     = lazy(() => import('./pages/HajjUmrah/HajjUmrahDetailPage'));
const OfflineHotelsPage       = lazy(() => import('./pages/OfflineHotels/OfflineHotelsPage'));
const AllPartnerHotelsPage    = lazy(() => import('./pages/OfflineHotels/AllPartnerHotelsPage'));
const OfflineHotelDetailPage  = lazy(() => import('./pages/OfflineHotels/OfflineHotelDetailPage'));
// /hotels-in/:slug — Destination Pages first (admin-published countries and
// cities); falls back to the partner-hotel city page inside the component.
const DestinationPage         = lazy(() => import('./pages/Destinations/DestinationPage'));
const OnboardingWizard        = lazy(() => import('./pages/Onboarding/OnboardingWizard'));
const B2BLogin         = lazy(() => import('./pages/B2B/B2BLogin'));
const B2BPortal        = lazy(() => import('./pages/B2B/B2BPortal'));
const B2BRegister      = lazy(() => import('./pages/B2B/B2BRegister'));
const B2BResetPassword = lazy(() => import('./pages/B2B/B2BResetPassword'));
const UmmrahPlannerPage = lazy(() => import('./pages/UmmrahPlanner/UmmrahPlannerPage'));
const WishlistPage      = lazy(() => import('./pages/Wishlist/WishlistPage'));
const NotFoundPage      = lazy(() => import('./pages/NotFound/NotFoundPage'));
// Billbed hotel DETAIL + booking. There is no separate Billbed list page any
// more — its hotels are listed on /hotels-partner alongside the partner ones,
// so this is only ever reached from a card there. Still gated on the `billbed`
// flag (off by default on every plan) and lazy-loaded, so tenants without the
// flag never download the chunk.
const BillbedHotelDetailPage = lazy(() => import('./pages/BillbedHotels/BillbedHotelDetailPage'));
const WebbedsOfflineHotelDetailPage = lazy(() => import('./pages/WebbedsOfflineHotels/WebbedsOfflineHotelDetailPage'));
const PaymentResult     = lazy(() => import('./pages/PaymentResult/PaymentResult'));

// Fallback Client ID from .env (used instantly before server responds)
const ENV_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Shared context so Navbar can know when a real clientId is available.
// Navbar's GoogleLoginBtn only renders when this is non-empty, ensuring
// useGoogleLogin() is only called when GoogleOAuthProvider is actually mounted.
export const GoogleReadyContext = React.createContext('');

// Only mount GoogleOAuthProvider when we have a real clientId.
// This prevents Google's GSI library from receiving an invalid client_id.
const GoogleWrapper = ({ children, googleClientId }) => {
  if (!googleClientId) return <>{children}</>;
  return <GoogleOAuthProvider clientId={googleClientId}>{children}</GoogleOAuthProvider>;
};

function App() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/private');
  // The dashboard itself gets its own top bar instead of the shop menu
  // (the Help Center under /private/help keeps the site navbar).
  const isDashboard = /^\/private\/?$/.test(location.pathname);
  // Booking-form pages: on PHONES the floating chat + WhatsApp bubbles sit on
  // top of the sticky price bar / submit button, so both hide there (mobile
  // only — desktop keeps them; every other page is untouched).
  const isBookingFormPage = /^\/(confirm-booking|hotelbeds-book|webbeds-book|flights\/detail)(\/|$)/.test(location.pathname);
  // A page can ask for the surrounding chrome to be dropped (see
  // BareLayoutContext). The 404 page uses it: it can't be identified by
  // pathname the way /private can, because it IS the catch-all route.
  const [bareLayout, setBareLayout] = useState(false);
  // The chat widget's chunk (~1,600-line component) was fetched and mounted on
  // every page during first load, for a floating button. It now waits until
  // the browser is idle (or 3 s at most), after the page itself has painted.
  const [chatIdle, setChatIdle] = useState(false);
  useEffect(() => {
    const go = () => setChatIdle(true);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(go, { timeout: 3000 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(go, 1500);
    return () => clearTimeout(t);
  }, []);

  // Scroll to top on every route change — useLayoutEffect fires before paint
  // so position:sticky elements calculate their offset from the correct (0)
  // scroll position rather than inheriting stale scroll from the previous page.
  // Also clear any body overflow lock left behind by modals/sheets — without this,
  // navigating away from a page with an open modal leaves body: overflow:hidden
  // which silently breaks position:sticky on the next page.
  useLayoutEffect(() => {
    // A page can claim this commit (child layout effects run before this
    // parent one) when it's restoring its own saved scroll position — e.g.
    // OfflineHotelsPage.js returning from a hotel detail page. Without this,
    // this reset fired right after that restore on every "back" navigation
    // and silently scrolled the reader back to the top.
    if (!consumeScrollRestoreClaim()) {
      window.scrollTo(0, 0);
    }
    document.body.style.overflow = '';
  }, [location.pathname]);

  // Single startup fetch: runs both requests in parallel, fires hm-settings-loaded once done.
  // /api/public-config  → googleClientId (needed before render) + siteBrand
  // /api/site-settings  → all tenant settings (hero, notice, theme, blog, etc.)
  // Both complete in one round-trip time instead of two sequential calls.
  const [googleClientId, setGoogleClientId] = useState(ENV_GOOGLE_CLIENT_ID);

  // Travel feature flags — read from localStorage, updated on hm-settings-loaded
  const [features, setFeatures] = useState(readSiteFeatures);
  // featuresReady: true once we have confirmed data (server-injected, localStorage, or API response).
  // Routes use this to avoid redirecting while still pending — we never want to bounce a user
  // away from /hotels before we even know if ratehawk is enabled.
  const [featuresReady, setFeaturesReady] = useState(() => !!window.__HM_FEATURES_READY__);

  useEffect(() => {
    const onLoaded = () => {
      setFeatures(readSiteFeatures());
      setFeaturesReady(true);
    };
    window.addEventListener('hm-settings-loaded', onLoaded);
    return () => window.removeEventListener('hm-settings-loaded', onLoaded);
  }, []);

  const ratehawkEnabled   = isFeatureEnabled(features, 'ratehawk');
  const webbedsEnabled    = isFeatureEnabled(features, 'webbeds');
  const hotelbedsEnabled  = isFeatureEnabled(features, 'hotelbeds');
  const anyHotelsEnabled  = ratehawkEnabled || webbedsEnabled || hotelbedsEnabled;
  const nearbyHotelsEnabled = anyHotelsEnabled && isFeatureEnabled(features, 'nearbyHotels');
  const destinationsEnabled = anyHotelsEnabled && isFeatureEnabled(features, 'destinations');

  useEffect(() => {
    Promise.allSettled([
      getPublicConfig(), // shared promise — pages reuse it instead of re-fetching
      fetch(`${API_BASE_URL}/api/site-settings`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/exchange-rates`).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/site-features`).then(r => r.json()),
    ]).then(([configResult, settingsResult, ratesResult, featuresResult]) => {
      // 1. Apply Google Client ID
      if (configResult.status === 'fulfilled') {
        const cfg = configResult.value;
        if (cfg.googleClientId) setGoogleClientId(cfg.googleClientId);
        // Write siteBrand into localStorage so SiteBrandContext picks it up
        if (cfg.siteBrand && (cfg.siteBrand.siteName || cfg.siteBrand.logoUrl)) {
          try { localStorage.setItem('hm_site_brand', JSON.stringify(cfg.siteBrand)); } catch {}
        }
        // Cache ops email for support links (e.g. ConfirmBookingPage)
        if (cfg.opsEmail) {
          try { localStorage.setItem('hm_ops_email', cfg.opsEmail); } catch {}
        }
      }
      // 2. Write all tenant settings into localStorage
      // Guard: only write when the response actually contains data. An empty object
      // means the site wasn't found (unregistered host) — in that case keep the
      // existing cached settings so the UI stays populated.
      // Credential blocks the server used to leak through /api/site-settings
      // (it now strips them). Browsers that loaded the site before the fix
      // still hold copies in localStorage — delete them.
      ['smtpConfig', 'smtp', 'socialConfig', 'telegramConfig', 'safepayConfig', 'whatsappConfig']
        .forEach(k => { try { localStorage.removeItem(k); } catch {} });
      if (settingsResult.status === 'fulfilled') {
        const { data } = settingsResult.value;
        if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          Object.entries(data).forEach(([key, value]) => {
            try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
          });
        }
      }
      // 3. Cache live exchange rates (USD-per-unit format, inverted from Frankfurter response)
      if (ratesResult.status === 'fulfilled' && ratesResult.value?.rates) {
        try { localStorage.setItem('hm_xrates', JSON.stringify(ratesResult.value.rates)); } catch {}
      }
      // 4. Update feature toggles from Marketing API
      // IMPORTANT: only persist when the site is actually registered. If the
      // server returns registered:false (e.g. a transient host-header mismatch on
      // the proxy), writing those all-false features would wipe the cached good
      // data and cause all feature-gated routes to redirect away — the "flash then
      // disappear" bug. We keep the existing localStorage value in that case so the
      // user continues seeing the correct content.
      if (featuresResult.status === 'fulfilled' && featuresResult.value?.features
          && featuresResult.value?.registered !== false) {
        try {
          localStorage.setItem('hm_travel_features', JSON.stringify(featuresResult.value.features));
          // Keep window.__HM_FEATURES__ in sync so readSiteFeatures() returns the latest
          // value on the next hm-settings-loaded refresh (same object reference avoids a
          // JSON.parse round-trip through localStorage).
          window.__HM_FEATURES__ = featuresResult.value.features;
          // Home page designs this site's plan includes — the Hero Banner
          // picker padlocks the rest (the server enforces the same list, so
          // this is only what the admin SEES, never what they can save).
          if (Array.isArray(featuresResult.value.allowedHomeDesigns)) {
            localStorage.setItem('hm_allowed_home_designs', JSON.stringify(featuresResult.value.allowedHomeDesigns));
          }
        } catch {}
      }
      // 5. Mark features as confirmed-loaded so isFeatureEnabled() stops
      //    defaulting to "show all" and uses the real values from this point on.
      //    Must be set BEFORE dispatching hm-settings-loaded so every listener
      //    that calls readSiteFeatures() / isFeatureEnabled() sees the ready flag.
      window.__HM_FEATURES_READY__ = true;
      // Signal all components to re-read from localStorage
      window.dispatchEvent(new CustomEvent('hm-settings-loaded'));
      // 6. Eagerly prefetch the most-visited page chunks while the browser is idle.
      //    This ensures first navigation is instant for all users, not just those
      //    who hover over nav links (which already triggers prefetch via routePrefetchHandler).
      //    Skipped for visitors on Save-Data, and only for pages this site
      //    actually has — it used to download (and execute) the Deals / Hajj /
      //    Visa chunks even on tenants with those features switched off.
      const idlePrefetch = () => {
        if (navigator.connection && navigator.connection.saveData) return;
        const live = readSiteFeatures();
        const byFeature = { '/deals': 'deals', '/hajj-umrah': 'hajjUmrah', '/visa': 'visa' };
        ['/deals', '/hajj-umrah', '/contact', '/about', '/visa']
          .filter(p => !byFeature[p] || isFeatureEnabled(live, byFeature[p]))
          .forEach(prefetchRoute);
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(idlePrefetch, { timeout: 4000 });
      } else {
        setTimeout(idlePrefetch, 2000);
      }
    });
  }, []);

  return (
    <GoogleReadyContext.Provider value={googleClientId}>
    <GoogleWrapper googleClientId={googleClientId}>
      <ThemeCustomProvider>
      <CookieConsentProvider>
      <SiteBrandProvider>
      <AuthProvider>
      <B2BAgentProvider>
      <HotelsProvider>
      <PageContentProvider>
      <BareLayoutContext.Provider value={setBareLayout}>
        <div className="flex flex-col min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}>
          <PageLoader />
          {/* bareLayout: a page has asked for the entire shell to get out of
              the way — see BareLayoutContext. Everything visual is skipped,
              leaving that page alone on the screen. DynamicSEO and PageLoader
              stay: neither renders anything. */}
          {!bareLayout && !isDashboard && <TopInfoBar />}
          {!bareLayout && (isDashboard ? <AdminTopBar /> : <Navbar />)}
          <DynamicSEO />
          {/* All lazy-loaded: wrapped in a single fallback-null Suspense so
              a slow chunk download doesn't blank the page. None of these
              are above-the-fold, so users never see the empty fallback. */}
          <Suspense fallback={null}>
            {!bareLayout && <CookieBanner />}
            {!bareLayout && <VisitorPopup />}
            {!bareLayout && <CampaignPopup />}
            {!isAdminPage && <CampaignThemeStyle />}{/* CAMPAIGN-TEMP */}
            {!bareLayout && <CampaignHeroFx />}{/* CAMPAIGN-TEMP */}
          </Suspense>
          {/* Global sign-in listener — any component anywhere in the tree can
              call `requestSignIn()` (from utils/wishlist) to open the sheet.
              Used by auth-gated actions like saving to wishlist when the user
              isn't signed in. Lazy SignInSheet bundle only loads if triggered. */}
          {!isAdminPage && !bareLayout && <SignInGateway />}
          {!isAdminPage && !bareLayout && <NoticeBar />}
          <main className="flex-grow">
            <PageErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/hotels" element={featuresReady && !anyHotelsEnabled ? <Navigate to="/" replace /> : <Hotels />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={featuresReady && features.blog === false ? <Navigate to="/" replace /> : <Blog />} />
              <Route path="/blog/:slug" element={featuresReady && features.blog === false ? <Navigate to="/" replace /> : <BlogPost />} />
              <Route path="/my-bookings" element={<PrivateRoute pageName="My Bookings" requireAdmin={false}><MyBookings /></PrivateRoute>} />
              {/* Safepay hosted-checkout landing — public (no auth) so the redirect always resolves */}
              <Route path="/payment/result" element={<PaymentResult />} />
              <Route path="/destinations" element={featuresReady && !destinationsEnabled ? <Navigate to="/" replace /> : <Destinations />} />
              <Route path="/experiences" element={featuresReady && features.tours === false ? <Navigate to="/" replace /> : <Experiences />} />
              <Route path="/search" element={featuresReady && !anyHotelsEnabled ? <Navigate to="/" replace /> : <SearchResults />} />
              <Route path="/nearby" element={featuresReady && !nearbyHotelsEnabled ? <Navigate to="/" replace /> : <NearbyHotelsPage />} />
              <Route path="/hotel-detail-data" element={featuresReady && !ratehawkEnabled ? <Navigate to="/" replace /> : <HotelDetailPage />} />
              <Route path="/confirm-booking" element={featuresReady && !ratehawkEnabled ? <Navigate to="/" replace /> : <ConfirmBookingPage />} />
              <Route path="/webbeds-hotel" element={featuresReady && !webbedsEnabled ? <Navigate to="/" replace /> : <WebBedsHotelDetail />} />
              <Route path="/webbeds-book"   element={featuresReady && !webbedsEnabled ? <Navigate to="/" replace /> : <WebBedsBookingForm />} />
              <Route path="/hotelbeds-hotel" element={featuresReady && !hotelbedsEnabled ? <Navigate to="/" replace /> : <HotelbedsHotelDetail />} />
              <Route path="/hotelbeds-book"  element={featuresReady && !hotelbedsEnabled ? <Navigate to="/" replace /> : <HotelbedsBookingForm />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/write-review" element={<WriteReviewPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/press" element={<PressPage />} />
              {/* Human-readable sitemap — the footer's "Sitemap" link. Never
                  feature-gated: it only ever lists what is already enabled. */}
              <Route path="/sitemap" element={<SitemapPage />} />
              <Route path="/support" element={featuresReady && features.support === false ? <Navigate to="/" replace /> : <SupportPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/deals" element={featuresReady && features.deals === false ? <Navigate to="/" replace /> : <DealsPage />} />
              <Route path="/deals/:id" element={featuresReady && features.deals === false ? <Navigate to="/" replace /> : <DealDetailPage />} />
              <Route path="/hajj-umrah" element={featuresReady && features.hajjUmrah === false ? <Navigate to="/" replace /> : <HajjUmrahPage />} />
              <Route path="/hajj-umrah/:id" element={featuresReady && features.hajjUmrah === false ? <Navigate to="/" replace /> : <HajjUmrahDetailPage />} />
              <Route path="/ummrah-planner" element={featuresReady && features.ummrahPlanner === false ? <Navigate to="/" replace /> : <UmmrahPlannerPage />} />
              <Route path="/hotels-partner" element={featuresReady && features.offlineHotels === false ? <Navigate to="/" replace /> : <OfflineHotelsPage />} />
              {/* Partner inventory ONLY — no Billbed/WebBeds search. Target of
                  "View all partner hotels"; /hotels-partner above keeps the
                  city drilldown with supplier hotels merged in. */}
              <Route path="/partner-hotels" element={featuresReady && features.offlineHotels === false ? <Navigate to="/" replace /> : <AllPartnerHotelsPage />} />
              {/* No feature gate: destination pages work on any site, and the
                  server already answers 404 for a slug with nothing behind it. */}
              <Route path="/hotels-in/:citySlug" element={<DestinationPage />} />
              {/* Optional trailing :slug? is the hotel-name SEO segment — the id
                  alone still resolves (legacy links), and the detail page then
                  canonicalizes the URL to the slugged form. */}
              <Route path="/hotels-partner/:id/:slug?" element={featuresReady && features.offlineHotels === false ? <Navigate to="/" replace /> : <OfflineHotelDetailPage />} />
              <Route path="/private" element={<PrivateRoute pageName="Private Dashboard"><PrivatePage /></PrivateRoute>} />
              {/* Admin Help Center: guides for every dashboard section (src/help). */}
              <Route path="/private/help" element={<PrivateRoute pageName="Help Center"><HelpCenterPage /></PrivateRoute>} />
              <Route path="/private/help/topic/:categoryId" element={<PrivateRoute pageName="Help Center"><HelpCenterPage /></PrivateRoute>} />
              <Route path="/private/help/:articleId" element={<PrivateRoute pageName="Help Center"><HelpCenterPage /></PrivateRoute>} />
              <Route path="/onboarding" element={<OnboardingWizard />} />
              <Route path="/b2b/login"            element={featuresReady && features.b2b === false ? <Navigate to="/" replace /> : <B2BLogin />} />
              <Route path="/b2b/register"         element={featuresReady && features.b2b === false ? <Navigate to="/" replace /> : <B2BRegister />} />
              <Route path="/b2b/portal"           element={featuresReady && features.b2b === false ? <Navigate to="/" replace /> : <B2BPortal />} />
              {/* Forgot + reset both render B2BResetPassword — the page toggles
                  to "set new password" mode when the URL has ?token=... */}
              <Route path="/b2b/forgot-password"  element={featuresReady && features.b2b === false ? <Navigate to="/" replace /> : <B2BResetPassword />} />
              <Route path="/b2b/reset-password"   element={featuresReady && features.b2b === false ? <Navigate to="/" replace /> : <B2BResetPassword />} />
              <Route path="/flights" element={featuresReady && features.flights === false ? <Navigate to="/" replace /> : <FlightSearch />} />
              {/* /flights/detail is the SHARED checkout — PKFARE offers hand off
                  here too, so it stays reachable when either flight feature is on. */}
              <Route path="/flights/detail" element={featuresReady && features.flights === false && features.pkfareFlights === false ? <Navigate to="/" replace /> : <FlightDetail />} />
              {/* PKFARE — fully separate flight search vertical */}
              <Route path="/pkfare-flights" element={featuresReady && features.pkfareFlights === false ? <Navigate to="/" replace /> : <PkfareSearch />} />
              <Route path="/tours" element={featuresReady && features.tours === false ? <Navigate to="/" replace /> : <ToursPage />} />
              <Route path="/tours/:productCode" element={featuresReady && features.tours === false ? <Navigate to="/" replace /> : <TourDetail />} />
              <Route path="/tours/:productCode/book" element={featuresReady && features.tours === false ? <Navigate to="/" replace /> : <TourBookingPage />} />
              <Route path="/visa" element={featuresReady && features.visa === false ? <Navigate to="/" replace /> : <VisaPage />} />
              <Route path="/visa/:destination" element={featuresReady && features.visa === false ? <Navigate to="/" replace /> : <VisaPage />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              {/* Billbed — 404s (via the catch-all) for any tenant without the flag */}
              <Route path="/makkah-madinah-hotels/:hotelId" element={featuresReady && features.billbed !== true ? <NotFoundPage /> : <BillbedHotelDetailPage />} />
              {/* WebBeds sold offline (manual reservations) — its own path, so it
                  can never collide with the live WebBeds routes above or with
                  Billbed's :hotelId segment. Same 404-unless-granted rule. */}
              <Route path="/makkah-madinah-stays/:hotelId" element={featuresReady && features.webbedsOffline !== true ? <NotFoundPage /> : <WebbedsOfflineHotelDetailPage />} />
              {/* ── 404 catch-all ──────────────────────────────────────────
                   Renders a real not-found page instead of bouncing to "/".
                   The old redirect made every unknown URL a soft 404: the
                   server answered 200 + index,follow + a self-canonical, so
                   junk paths got indexed as homepage duplicates. The server
                   now sends a genuine 404 status for these paths. ── */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            </PageErrorBoundary>
          </main>
          {/* Per-page FAQ block — one mount point serves every route. Renders
              nothing unless an admin has added questions for the current page
              (Pages Management → <page> → Content). Sits between the page and
              the footer, which is the conventional spot for it. */}
          {!isAdminPage && <RouteFaqSection />}
          <div className={isBookingFormPage ? 'hm-hide-float-mobile' : undefined}>
            {/* When the AI chatbot FAB is absent (feature off / admin / bare
                layout), WhatsApp takes the prime bottom corner itself. */}
            <WhatsAppButton primary={!(!isAdminPage && !bareLayout && isFeatureEnabled(features, 'ai'))} />
            {/* Phones, when Site Widgets → Chat Buttons says "One Help button":
                replaces the two buttons above with one menu button. */}
            {!isAdminPage && !bareLayout && <HelpLauncher chatbotOn={isFeatureEnabled(features, 'ai')} />}
          </div>
          {/* iOS PWA install prompt — only renders on iPhone / iPad that
              hasn't already installed, and only outside admin. Self-dismisses
              and self-hides on non-iOS devices, so it's safe site-wide. */}
          {IS_IOS && !isAdminPage && !bareLayout && <Suspense fallback={null}><IOSInstallPrompt /></Suspense>}
          {/* "Get our app" card — Android visitors, when the site has
              published its app (Admin → Branding → Android App). */}
          {!isAdminPage && !bareLayout && !isBookingFormPage && <Suspense fallback={null}><AndroidAppBanner /></Suspense>}
          {/* Lazy peripherals — null fallback because they appear below the
              fold; users see Footer when they scroll, ChatBot when they
              hit the trigger button. Suspense never blocks the page. */}
          {/* Persistent mobile bottom navigation — feature-toggle aware,
              auto-hides on admin / modal-open / desktop. Mounted OUTSIDE
              the lazy Suspense so it's instantly available on first paint
              and doesn't wait for a chunk to download. */}
          {!isAdminPage && !bareLayout && <MobileBottomNav />}
          <Suspense fallback={null}>
            {chatIdle && !isAdminPage && !bareLayout && isFeatureEnabled(features, 'ai') && (
              <div className={isBookingFormPage ? 'hm-hide-float-mobile' : undefined}>
                <AIChatBot />
              </div>
            )}
            {!isAdminPage && !bareLayout && <Footer />}
          </Suspense>
        </div>
      </BareLayoutContext.Provider>
      </PageContentProvider>
      </HotelsProvider>
      </B2BAgentProvider>
      </AuthProvider>
      </SiteBrandProvider>
      </CookieConsentProvider>
      </ThemeCustomProvider>
    </GoogleWrapper>
    </GoogleReadyContext.Provider>
  );
}

export default App;
